import { useCallback, useMemo } from 'react'
import type { AddFromFilterRequest } from '../types/case-container'
import { useMapSetFetch } from './use-map-set/use-map-set-fetch'
import { useMapSetSync } from './use-map-set/use-map-set-sync'
import { useMapSetMutations } from './use-map-set/use-map-set-mutations'
import { useMapSetBulk } from './use-map-set/use-map-set-bulk'

export interface UseMapSetResult {
  endpointIds: Set<string>
  count: number
  loading: boolean
  syncing: boolean
  error: string | null
  isInSet: (id: string) => boolean
  add: (id: string) => void
  remove: (id: string) => void
  toggle: (id: string) => void
  addMany: (ids: string[]) => void
  removeMany: (ids: string[]) => void
  addFiltered: (filter: AddFromFilterRequest) => Promise<void>
  clearAll: () => Promise<void>
  refetch: () => Promise<void>
}

export function useMapSet(caseId: string | null): UseMapSetResult {
  const fetchState = useMapSetFetch(caseId)
  const sync = useMapSetSync({ caseId, setError: fetchState.setError })
  const mutations = useMapSetMutations({
    endpointIds: fetchState.endpointIds,
    setEndpointIds: fetchState.setEndpointIds,
    pendingChanges: sync.pendingChanges,
    scheduleSync: sync.scheduleSync,
  })
  const bulk = useMapSetBulk({
    caseId,
    setEndpointIds: fetchState.setEndpointIds,
    pendingChanges: sync.pendingChanges,
    debounceTimer: sync.debounceTimer,
    setSyncing: sync.setSyncing,
    setError: fetchState.setError,
  })

  const isInSet = useCallback(
    (id: string) => fetchState.endpointIds.has(id),
    [fetchState.endpointIds]
  )
  const count = useMemo(() => fetchState.endpointIds.size, [fetchState.endpointIds])

  return {
    endpointIds: fetchState.endpointIds,
    count,
    loading: fetchState.loading,
    syncing: sync.syncing,
    error: fetchState.error,
    isInSet,
    add: mutations.add,
    remove: mutations.remove,
    toggle: mutations.toggle,
    addMany: mutations.addMany,
    removeMany: mutations.removeMany,
    addFiltered: bulk.addFiltered,
    clearAll: bulk.clearAll,
    refetch: fetchState.refetch,
  }
}
